import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  List,
  ListItem,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  Slider,
  Grid,
  Avatar,
  Box,
  AppBar,
  Toolbar,
  Tooltip,
} from "@material-ui/core";

import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";

import MainListItems from "./MainListItems";
import AccordionMenu from "../components/AccordionMenu";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import useAuth from "../hooks/useAuth.js";
import BackdropLoading from "../components/BackdropLoading";
import toastError from "../errors/toastError";

//import logo from "../assets/logo.png";
import { SocketContext } from "../context/Socket/SocketContext";

import { useWebInfo } from "../context/WebInfoContext";

import ColorModeContext from "../layout/themeContext";
import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';
import VolumeDownIcon from '@material-ui/icons/VolumeDown';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import LoggerConfig from "../components/LoggerConfig";
import SettingsIcon from '@material-ui/icons/Settings';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import { getVersionNumber } from "../config/version";
import { getProfilePicUrl } from "../services/api";
import { getInitials } from "../helpers/getInitials";
import { generateColor } from "../helpers/colorGenerator";
import AnnouncementsOnLogin from "../components/AnnouncementsOnLogin";
import UpdateVersionModal from "../components/UpdateVersionModal";
import ChatPopover from "../pages/Chat/ChatPopover";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { useHistory } from "react-router-dom";

const drawerWidth = 280; // ✅ AUMENTADO PARA MÁS ESPACIO EN SUB-MENÚS

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    maxHeight: "100vh",
    overflow: "hidden", // ✅ Evitar scroll en el root
    backgroundColor: theme.palette.fancyBackground,
    '& .MuiButton-outlinedPrimary': {
      color: theme.mode === 'light' ? '#FFF' : '#FFF',
	  backgroundColor: theme.mode === 'light' ? '#1e3a8a' : '#1f2937',
      //border: theme.mode === 'light' ? '1px solid rgba(0 124 102)' : '1px solid rgba(255, 255, 255, 0.5)',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: theme.mode === 'light' ? '#1e3a8a' : '#FFF',
    }
  },
  avatar: {
    width: "100%",
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
    color: theme.palette.dark.main,
    background: theme.palette.barraSuperior,
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 8px",
    minHeight: "48px",
    [theme.breakpoints.down("sm")]: {
      height: "48px"
    },
    '& img': {
      transition: 'opacity 0.3s ease',
    },
    '&.collapsed img': {
      opacity: 0,
      width: 0,
      height: 0,
      overflow: 'hidden',
    },
    '&.collapsed': {
      justifyContent: "center",
    }
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarFixed: {
    position: "fixed",
    top: 0,
    right: 0,
    left: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)',
    overflow: "hidden", // ✅ Eliminar scroll de la AppBar
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      left: 0,
      width: "100%"
    }
  },
  appBarFixedClosed: {
    left: theme.spacing(7),
    width: `calc(100% - ${theme.spacing(7)}px)`,
    [theme.breakpoints.up("sm")]: {
      left: theme.spacing(9),
      width: `calc(100% - ${theme.spacing(9)}px)`,
    },
    [theme.breakpoints.down("sm")]: {
      left: 0,
      width: "100%"
    }
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },
  toolbarFixed: {
    minHeight: "56px",
    maxHeight: "56px",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between", // ✅ Cambiado para distribuir contenido
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden", // ✅ Eliminar scroll innecesario
    '& .MuiIconButton-root': {
      color: theme.palette.text.primary + ' !important',
      '&:hover': {
        backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
      }
    }
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    fontSize: 14,
    color: "white",
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    // ✅ CORREGIDO: Fondo dinámico del drawer según tema
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeInOut, // ✅ TRANSICIÓN MÁS SUAVE
      duration: theme.transitions.duration.standard, // ✅ DURACIÓN ESTÁNDAR
    }),
    [theme.breakpoints.down("sm")]: {
      width: "100%"
    },
    ...theme.scrollbarStylesSoft
  },
  drawerPaperClose: {
    overflowX: "hidden",
    // ✅ CORREGIDO: Fondo dinámico del drawer cerrado según tema
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeInOut, // ✅ TRANSICIÓN MÁS SUAVE
      duration: theme.transitions.duration.standard, // ✅ DURACIÓN ESTÁNDAR
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%"
    }
  },
  appBarSpacer: {
    minHeight: "48px",
  },
  content: {
    flex: 1,
    height: "calc(100vh - 56px)", // ✅ Altura exacta menos la AppBar
    maxHeight: "calc(100vh - 56px)", // ✅ Evitar overflow
    overflowY: "auto", // ✅ Solo scroll vertical cuando sea necesario
    overflowX: "hidden", // ✅ Sin scroll horizontal
    display: "flex",
    flexDirection: "column",
    marginTop: "56px", // ✅ Espacio para la AppBar fija
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeInOut, // ✅ TRANSICIÓN SUAVE
      duration: theme.transitions.duration.standard, // ✅ DURACIÓN ESTÁNDAR
    }),
    // ✅ Ocultar scrollbar cuando no hay contenido que hacer scroll
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.mode === 'dark' ? '#4B5563' : '#cbd5e0',
      borderRadius: '4px',
      '&:hover': {
        background: theme.mode === 'dark' ? '#6B7280' : '#a0aec0',
      }
    },
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column"
  },
  containerWithScroll: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    '& .MuiListItemIcon-root': {
      minWidth: '40px',
      justifyContent: 'center',
      '& svg': {
        fontSize: '20px',
      },
    },
  },
  NotificationsPopOver: {
    // color: theme.barraSuperior.secondary.main,
  },
  logo: {
    width: "80%",
    height: "auto",
    maxWidth: 180,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: "80%",
      maxWidth: 180,
    },
    logo: theme.logo
  },
}));

const LoggedInLayout = ({ children, themeToggle }) => {
  const classes = useStyles();
  const history = useHistory();
  const [userModalOpen, setUserModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [anchorEl, setAnchorEl] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [menuOpen, setMenuOpen] = useState(false);
  const [loggerConfigOpen, setLoggerConfigOpen] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [updateVersionModalOpen, setUpdateVersionModalOpen] = useState(false);
  const { loading } = useContext(AuthContext);
  const { handleLogout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  
  // ✅ CONFIGURACIÓN PARA ALTERNAR ENTRE MENÚS
  const [useNewMenu] = useState(true); // ✅ Nuevo menú por defecto
  // const [dueDate, setDueDate] = useState("");
  const { user } = useContext(AuthContext);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);

  const [volume, setVolume] = useState(Number(localStorage.getItem("volume")) || 1);

  useEffect(() => {
    localStorage.setItem("volume", volume);
  }, [volume]);

  const { loadWebInfo } = useWebInfo();

  //################### CODIGOS DE TESTE #########################################
  // useEffect(() => {
  //   navigator.getBattery().then((battery) => {
  //     console.log(`Battery Charging: ${battery.charging}`);
  //     console.log(`Battery Level: ${battery.level * 100}%`);
  //     console.log(`Charging Time: ${battery.chargingTime}`);
  //     console.log(`Discharging Time: ${battery.dischargingTime}`);
  //   })
  // }, []);

  // useEffect(() => {
  //   const geoLocation = navigator.geolocation

  //   geoLocation.getCurrentPosition((position) => {
  //     let lat = position.coords.latitude;
  //     let long = position.coords.longitude;

  //     console.log('latitude: ', lat)
  //     console.log('longitude: ', long)
  //   })
  // }, []);

  // useEffect(() => {
  //   const nucleos = window.navigator.hardwareConcurrency;

  //   console.log('Nucleos: ', nucleos)
  // }, []);

  // useEffect(() => {
  //   console.log('userAgent', navigator.userAgent)
  //   if (
  //     navigator.userAgent.match(/Android/i)
  //     || navigator.userAgent.match(/webOS/i)
  //     || navigator.userAgent.match(/iPhone/i)
  //     || navigator.userAgent.match(/iPad/i)
  //     || navigator.userAgent.match(/iPod/i)
  //     || navigator.userAgent.match(/BlackBerry/i)
  //     || navigator.userAgent.match(/Windows Phone/i)
  //   ) {
  //     console.log('é mobile ', true) //celular
  //   }
  //   else {
  //     console.log('não é mobile: ', false) //nao é celular
  //   }
  // }, []);
  //##############################################################################

  const socketManager = useContext(SocketContext);

  // ✅ NUEVO: Escuchar cambios en las configuraciones del sitio web
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleSettingsUpdate = (data) => {
      if (data.action === "update") {
        // Si se actualiza alguna configuración del sitio web, recargar la información
        if (['websiteTitle', 'websiteDescription', 'websiteKeyword'].includes(data.setting.key)) {
          loadWebInfo();
        }
      }
    };

    // ✅ LIMPIAR LISTENER ANTERIOR PARA EVITAR DUPLICADOS
    socket.off(`company-${companyId}-settings`, handleSettingsUpdate);
    socket.on(`company-${companyId}-settings`, handleSettingsUpdate);

    return () => {
      // ✅ SOLO REMOVER LISTENERS, NO DESCONECTAR EL SOCKET COMPARTIDO
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-settings`, handleSettingsUpdate);
      }
    };
  }, [socketManager, loadWebInfo]);

  useEffect(() => {
    if (document.body.offsetWidth > 1200) {
      setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId");
    let timeoutId = null;

    const socket = socketManager.getSocket(companyId);

    const handleAuthEvent = (data) => {
      if (data.user.id === +userId) {
        toastError("Sua conta foi acessada em outro computador.");
        timeoutId = setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    };

    // ✅ LIMPIAR LISTENER ANTERIOR PARA EVITAR DUPLICADOS
    socket.off(`company-${companyId}-auth`, handleAuthEvent);
    socket.on(`company-${companyId}-auth`, handleAuthEvent);

    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      // ✅ SOLO REMOVER LISTENERS, NO DESCONECTAR EL SOCKET COMPARTIDO
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-auth`, handleAuthEvent);
        socket.off(`company-${companyId}-notification`);
        socket.off("ready");
        socket.off("connect");
        socket.off("disconnect");
      }
      // ✅ LIMPIAR INTERVAL Y TIMEOUT PARA EVITAR MEMORY LEAKS
      if (interval) {
        clearInterval(interval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [socketManager]);

  // eslint-disable-next-line no-unused-vars
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleLogoutClick = () => {
    handleLogout();
    handleCloseMenu();
  };

  const handleSettingsMenu = (event) => {
    setSettingsAnchorEl(event.currentTarget);
    setSettingsMenuOpen(true);
  };

  const handleCloseSettingsMenu = () => {
    setSettingsAnchorEl(null);
    setSettingsMenuOpen(false);
  };



  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  const toggleColorMode = () => {
    colorMode.toggleColorMode();
  }

  if (loading) {
    return <BackdropLoading />;
  }
  
  // ✅ SOLUCIONADO: Logo sin Math.random() para evitar bucle infinito
  const logo = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/public/logotipos/interno.png`;

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div className={clsx(classes.toolbarIcon, !drawerOpen && 'collapsed')}>
          {drawerOpen && (
            <img src={logo} style={{ margin: "0 auto" , width: "50%"}} alt={`${(typeof process !== 'undefined' && process.env) ? process.env.REACT_APP_NAME_SYSTEM : 'Sistema'}`} />
          )}
          <IconButton 
            onClick={() => setDrawerOpen(!drawerOpen)}
            style={{
              transition: 'transform 0.3s ease-in-out',
              transform: drawerOpen ? 'rotate(0deg)' : 'rotate(180deg)',
              marginLeft: drawerOpen ? 0 : 'auto',
              marginRight: drawerOpen ? 0 : 'auto',
            }}
            title={drawerOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List className={classes.containerWithScroll}>
          {useNewMenu ? (
            <AccordionMenu drawerClose={drawerClose} drawerOpen={drawerOpen} />
          ) : (
            <MainListItems drawerClose={drawerClose} collapsed={!drawerOpen} />
          )}
        </List>
        <Divider />
        {/* ✅ Información del usuario y versión movidas a la barra superior */}
        {/* Menú de configuración */}
        <Menu
          id="settings-menu"
          anchorEl={settingsAnchorEl}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          open={settingsMenuOpen}
          onClose={handleCloseSettingsMenu}
          PaperProps={{
            style: {
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
              boxShadow: theme.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.15)',
              minWidth: '200px',
            },
          }}
        >
          <MenuItem 
            onClick={() => {
              toggleColorMode();
              handleCloseSettingsMenu();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.palette.background.paper;
            }}
            style={{ 
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            {theme.mode === 'dark' ? (
              <Brightness7Icon style={{ marginRight: 8, color: theme.palette.text.primary }} />
            ) : (
              <Brightness4Icon style={{ marginRight: 8, color: theme.palette.text.primary }} />
            )}
            {theme.mode === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}
          </MenuItem>
          <MenuItem 
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.palette.background.paper;
            }}
            style={{ 
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            <Grid container spacing={2} alignItems="center" style={{ width: '100%' }}>
              <Grid item>
                <VolumeDownIcon style={{ color: theme.palette.text.primary }} />
              </Grid>
              <Grid item xs>
                <Slider
                  value={volume}
                  onChange={(e, newValue) => setVolume(newValue)}
                  min={0}
                  max={1}
                  step={0.1}
                  style={{ color: theme.palette.text.primary }}
                />
              </Grid>
              <Grid item>
                <VolumeUpIcon style={{ color: theme.palette.text.primary }} />
              </Grid>
            </Grid>
          </MenuItem>
          <Divider />
          <MenuItem 
            onClick={() => {
              handleCloseSettingsMenu();
              setLoggerConfigOpen(true);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.palette.background.paper;
            }}
            style={{ 
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            <SettingsIcon style={{ marginRight: 8, color: theme.palette.text.primary }} />
            Logs
          </MenuItem>
        </Menu>
        {/* Versión en la parte inferior del drawer - Fuera del área de scroll */}
        <Box style={{ 
          padding: drawerOpen ? '8px 12px' : '4px 2px',
          textAlign: 'center',
          borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`
        }}>
          <Typography style={{ 
            fontSize: drawerOpen ? "10px" : "8px", 
            fontWeight: "bold",
            color: "#df3333",
            lineHeight: drawerOpen ? "normal" : "1.2",
            wordBreak: drawerOpen ? "normal" : "break-word",
            overflow: drawerOpen ? "visible" : "hidden",
            textOverflow: drawerOpen ? "clip" : "ellipsis",
          }}>
            {drawerOpen ? `VERSIÓN ${getVersionNumber()}` : `V${getVersionNumber()}`}
          </Typography>
        </Box>
      </Drawer>
      {/* ✅ BARRA SUPERIOR FIJA - Restaurada para mostrar notificaciones */}
      <AppBar 
        position="fixed" 
        className={clsx(classes.appBarFixed, !drawerOpen && classes.appBarFixedClosed)}
      >
        <Toolbar className={classes.toolbarFixed}>
          <Box style={{ flex: 1 }} /> {/* Espaciador a la izquierda */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            {/* Chat Interno */}
            <Tooltip title="Chat Interno" arrow>
              <span>
                <ChatPopover />
              </span>
            </Tooltip>
            
            {/* Ayuda */}
            <Tooltip title="Ayuda" arrow>
              <IconButton
                onClick={() => history.push("/helps")}
                size="small"
                style={{
                  padding: '4px',
                  color: theme.palette.text.primary,
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.palette.primary.main;
                  e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = theme.palette.text.primary;
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Anuncios */}
            <Tooltip title="Anuncios" arrow>
              <span>
                <AnnouncementsPopover />
              </span>
            </Tooltip>
            
            {/* Notificaciones de WhatsApp */}
            <Tooltip title="Notificaciones de WhatsApp" arrow>
              <span>
                <NotificationsPopOver volume={volume} customStyle={{
                  padding: '4px',
                  color: theme.palette.text.primary,
                }} />
              </span>
            </Tooltip>
            
            {/* Configuración */}
            <Tooltip title="Configuración" arrow>
              <IconButton
                onClick={handleSettingsMenu}
                size="small"
                style={{
                  padding: '4px',
                  color: theme.palette.text.primary,
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.palette.text.primary;
                  e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = theme.palette.text.primary;
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Actualizar Versión - Solo para usuarios con companyId: 1 y super: true */}
            {user?.companyId === 1 && user?.super === true && (
              <Tooltip title="Actualizar Versión" arrow>
                <IconButton
                  onClick={() => setUpdateVersionModalOpen(true)}
                  size="small"
                  style={{
                    padding: '4px',
                    color: theme.palette.text.primary,
                    minWidth: '32px',
                    width: '32px',
                    height: '32px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = theme.palette.primary.main;
                    e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = theme.palette.text.primary;
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <SystemUpdateAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            {/* Salir */}
            <Tooltip title="Salir" arrow>
              <IconButton
                onClick={handleLogoutClick}
                size="small"
                style={{
                  padding: '4px',
                  color: theme.palette.text.primary,
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.palette.error.main;
                  e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = theme.palette.text.primary;
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <ExitToAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Separador visual */}
            <Box style={{ 
              width: '1px', 
              height: '32px', 
              backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
              margin: `0 ${theme.spacing(0.5)}px`
            }} />
            
            {/* Información del Usuario - Lado Derecho */}
            <Box style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginLeft: theme.spacing(0.5) }}>
              <Avatar
                src={
                  user?.profilePicUrl && 
                  !user.profilePicUrl.includes("nopicture.png")
                    ? getProfilePicUrl(user.profilePicUrl)
                    : null
                }
                onClick={handleOpenUserModal}
                style={{
                  width: 32,
                  height: 32,
                  backgroundColor: generateColor(user?.name || ""),
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "12px",
                  border: `2px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
                  boxShadow: theme.mode === 'dark' 
                    ? '0 2px 6px rgba(0,0,0,0.2)' 
                    : '0 2px 6px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onError={(e) => {
                  if (e.target) {
                    e.target.style.display = 'none';
                  }
                }}
              >
                {getInitials(user?.name || "")}
              </Avatar>
              {drawerOpen && (
                <Box style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Typography 
                    style={{ 
                      fontSize: "11px",
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      lineHeight: 1.2,
                    }}
                  >
                    User:
                  </Typography>
                  {user?.name && (
                    <Typography 
                      style={{ 
                        fontSize: "13px",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        lineHeight: 1.2,
                        marginTop: '2px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '120px',
                      }}
                    >
                      {user.name}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <LoggerConfig
        open={loggerConfigOpen}
        onClose={() => setLoggerConfigOpen(false)}
      />
      <UpdateVersionModal
        open={updateVersionModalOpen}
        onClose={() => setUpdateVersionModalOpen(false)}
      />
      <main className={classes.content}>

        {children ? children : null}
      </main>
      <AnnouncementsOnLogin />
    </div>
  );
};

export default LoggedInLayout;
